import React, { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import type { QuizScript, QuizScriptApiInterface } from '../../types/quizScript'
import type { QuizLocation } from '../../types/configuration'
import type { QuizMode } from '../quiz-types/quiz-mode-context'
import { useQuizStyleVariant } from '../quiz-types/shared/quiz-style-context'
import { cn } from '../../lib/utils'
import { QuizContainer } from './QuizContainer'

// Scope context for Product/Module level quizzes
export interface ScopeContext {
  scopeType: 'product' | 'module'
  scopeId: number
  activityType: 'start' | 'end'
}

export interface QuizResponseResult {
  isCorrect: boolean
  corrects?: boolean[]
  correctAnswers?: any[]
  description?: { text?: string; imageUrl?: string; videoUrl?: string }
}

export interface QuizResponseData {
  quizId: string
  sequenceNumber: number
  scriptId?: string
  answers: any[]
  timeSpentSec?: number
  elapsedMs?: number
}

interface QuizSessionProps {
  contentId: number
  contentTitle?: string
  onComplete?: (stats: { correct: number; total: number }) => void
  onClose?: () => void
  initialIndex?: number
  quizLocation?: QuizLocation
  quizList?: QuizScript[]
  onlyIncorrect?: boolean
  quizScriptApi: QuizScriptApiInterface
  // Optional scope context for Product/Module level quizzes
  scopeContext?: ScopeContext
  // Learning Session API integration (optional)
  sessionId?: number           // quiz_session_progress.id for quiz-response API
  componentProgressId?: number // for complete API
  onQuizResponse?: (data: QuizResponseData) => Promise<QuizResponseResult>
  onSessionComplete?: () => Promise<void>
  // Hint 표시 여부 (Configuration에서 제어)
  showHint?: boolean
  assessmentMode?: boolean
  assessmentActionPlacement?: 'fixed-bottom' | 'card-end'
  mode?: QuizMode
}

export const QuizSession: React.FC<QuizSessionProps> = ({
  contentId,
  contentTitle,
  onComplete,
  onClose,
  initialIndex = 0,
  quizLocation,
  quizList: providedQuizList,
  onlyIncorrect: _onlyIncorrect = false,
  quizScriptApi,
  sessionId,
  componentProgressId,
  onQuizResponse,
  onSessionComplete,
  showHint,
  assessmentMode = false,
  assessmentActionPlacement = 'fixed-bottom',
  mode,
}) => {
  const { t } = useTranslation('quiz')
  const styleVariant = useQuizStyleVariant()
  const isAiq = styleVariant === 'aiq'
  const isCardEndAssessment = assessmentMode && assessmentActionPlacement === 'card-end'
  const usesAssessmentScreenLayout = isAiq || isCardEndAssessment
  const [quizList, setQuizList] = useState<QuizScript[] | null>(providedQuizList || null)
  const [currentIndex, setCurrentIndex] = useState(initialIndex)
  const [results, setResults] = useState<Map<string, { isCorrect: boolean; score: number }>>(new Map())
  // Assessment submission and advance can happen in one React batch. Keep the
  // latest submitted results available to completion before the next render.
  const resultsRef = useRef(results)
  const [isLoading, setIsLoading] = useState(!providedQuizList)
  const [error, setError] = useState<string | null>(null)
  const [isCompleted, setIsCompleted] = useState(false)
  const [solveStatus, setSolveStatus] = useState<(boolean | null)[]>([])

  useEffect(() => {
    setSolveStatus(new Array(quizList?.length ?? 0).fill(null))
  }, [quizList?.length])

  useEffect(() => {
    if (providedQuizList) {
      setQuizList(providedQuizList)
      setError(null)
      setIsLoading(false)
      return
    }
    loadQuizzes()
  }, [contentId, quizLocation, providedQuizList])

  useEffect(() => {
    if (initialIndex !== undefined) {
      setCurrentIndex(initialIndex)
    }
  }, [initialIndex])

  const loadQuizzes = async () => {
    try {
      setIsLoading(true)
      setError(null)
      if (!quizScriptApi.getContentQuizzes) {
        setQuizList([])
        return
      }
      const data = await quizScriptApi.getContentQuizzes(contentId, quizLocation, false)
      setQuizList(data)
    } catch (err) {
      setError(t('session.failedToLoad'))
      console.error(err)
    } finally {
      setIsLoading(false)
    }
  }

  const getQuizKey = (quiz: QuizScript, index: number): string => {
    return quiz.quizId || quiz.scriptId || String(index)
  }

  const handleQuizComplete = (result: { quizId: string; isCorrect: boolean; score: number }) => {
    const nextResults = new Map(resultsRef.current).set(result.quizId, { isCorrect: result.isCorrect, score: result.score })
    resultsRef.current = nextResults
    setResults(nextResults)
    setSolveStatus((prev) => {
      const next = [...prev]
      next[currentIndex] = result.isCorrect
      return next
    })
  }

  const handleNext = () => {
    if (quizList && currentIndex < quizList.length - 1) {
      setCurrentIndex((prev) => prev + 1)
    } else {
      finishSession()
    }
  }

  const handlePrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex((prev) => prev - 1)
    }
  }

  const finishSession = async () => {
    // Snapshot this attempt before persistence; a later retake must not change
    // the completed attempt reported back to the host.
    const correct = Array.from(resultsRef.current.values()).filter((r) => r.isCorrect).length
    const total = quizList?.length ?? 0
    setIsCompleted(true)
    try {
      // Use new Learning Session API if available
      if (componentProgressId && onSessionComplete) {
        await onSessionComplete()
      }

      onComplete?.({ correct, total })
    } catch (err) {
      console.error('Failed to complete session:', err)
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="w-12 h-12 rounded-full border-4 border-indigo-500 animate-spin border-t-transparent"></div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] text-center">
        <svg
          className="mb-4 w-16 h-16 text-red-400"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
          />
        </svg>
        <p className="mb-4 text-red-600">{error}</p>
        <button
          onClick={loadQuizzes}
          className="px-4 py-2 text-white bg-indigo-600 rounded-lg hover:bg-indigo-700"
        >
          {t('session.retry')}
        </button>
      </div>
    )
  }

  const displayQuizList = quizList

  if (!displayQuizList || displayQuizList.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] text-center">
        <svg
          className="mb-4 w-16 h-16 text-gray-400"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
          />
        </svg>
        <p className="text-gray-600">
          {t('session.noQuizForContent')}
        </p>
      </div>
    )
  }

  if (isCompleted && assessmentMode) {
    return (
      <div className="flex items-center justify-center min-h-[300px]">
        <div className="w-10 h-10 rounded-full border-4 border-[#5AC6C8] animate-spin border-t-transparent" />
      </div>
    )
  }

  if (isCompleted) {
    const correctCount = Array.from(results.values()).filter((r) => r.isCorrect).length
    const totalCount = displayQuizList.length
    const percentage = Math.round((correctCount / totalCount) * 100)

    return (
      <div className="p-8 mx-auto max-w-2xl">
        <div className="p-8 text-center bg-white rounded-2xl shadow-xl">
          <div
            className={`w-24 h-24 mx-auto rounded-full flex items-center justify-center mb-6 ${
              percentage >= 80
                ? 'bg-green-100'
                : percentage >= 50
                  ? 'bg-yellow-100'
                  : 'bg-red-100'
            }`}
          >
            <span
              className={`text-4xl font-bold ${
                percentage >= 80
                  ? 'text-green-600'
                  : percentage >= 50
                    ? 'text-yellow-600'
                    : 'text-red-600'
              }`}
            >
              {percentage}%
            </span>
          </div>

          <h2 className="mb-2 text-2xl font-bold text-gray-900">{t('session.completed')}</h2>
          <p className="mb-6 text-gray-600">
            {t('session.resultSummary', { totalCount, correctCount })}
          </p>

          {/* Progress Bar */}
          <div className="mb-6 w-full h-4 bg-gray-200 rounded-full">
            <div
              className={`h-4 rounded-full transition-all duration-500 ${
                percentage >= 80
                  ? 'bg-green-500'
                  : percentage >= 50
                    ? 'bg-yellow-500'
                    : 'bg-red-500'
              }`}
              style={{ width: `${percentage}%` }}
            />
          </div>

          {/* Result Message */}
          <div
            className={`p-4 rounded-lg mb-6 ${
              percentage >= 80
                ? 'bg-green-50 text-green-800'
                : percentage >= 50
                  ? 'bg-yellow-50 text-yellow-800'
                  : 'bg-red-50 text-red-800'
            }`}
          >
            {percentage >= 80 ? (
              <p>{t('session.feedbackExcellent')}</p>
            ) : percentage >= 50 ? (
              <p>{t('session.feedbackGood')}</p>
            ) : (
              <p>{t('session.feedbackEncourage')}</p>
            )}
          </div>

          <div className="flex gap-4 justify-center">
            <button
              onClick={() => {
                setIsCompleted(false)
                setCurrentIndex(0)
                resultsRef.current = new Map()
                setResults(resultsRef.current)
                setSolveStatus(new Array(quizList?.length ?? 0).fill(null))
              }}
              className="px-6 py-3 font-medium text-indigo-700 bg-indigo-100 rounded-lg hover:bg-indigo-200"
            >
              {t('session.retake')}
            </button>
            {onClose && (
              <button
                onClick={onClose}
                className="px-6 py-3 font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700"
              >
                {t('session.done')}
              </button>
            )}
          </div>
        </div>
      </div>
    )
  }

  const currentQuiz = displayQuizList[currentIndex]
  const currentQuizKey = getQuizKey(currentQuiz, currentIndex)
  const currentResult = results.get(currentQuizKey)

  return (
    <div
      className={cn(
        usesAssessmentScreenLayout
          ? cn(
              'flex min-h-dvh w-full flex-col items-center gap-10 px-4 pt-6',
              isCardEndAssessment ? 'bg-[#f0f3f7] pb-10' : 'bg-lc-bg-secondary',
            )
          : 'mx-auto max-w-3xl',
      )}
    >
      {/* Header */}
      <div className={cn(usesAssessmentScreenLayout ? 'flex w-full max-w-[720px] flex-col gap-3' : 'mb-6')}>
        <div className={cn('flex items-center justify-between', !usesAssessmentScreenLayout && 'mb-4')}>
          <h2 className={cn(usesAssessmentScreenLayout ? 'text-lc-v3-h5 font-bold text-lc-text-primary' : 'text-xl font-bold text-gray-900')}>
            {contentTitle || t('session.title')}
          </h2>
          {usesAssessmentScreenLayout ? (
            <span className="text-lc-v3-label-md font-semibold text-lc-text-secondary tabular-nums">
              {currentIndex + 1} / {displayQuizList.length}
            </span>
          ) : onClose ? (
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          ) : null}
        </div>

        {/* Progress Bar */}
        <div className={cn(usesAssessmentScreenLayout ? 'h-2 w-full overflow-hidden rounded bg-[#d8dee6]' : 'flex gap-4 items-center')}>
          <div className={cn(usesAssessmentScreenLayout ? 'h-full w-full' : 'flex-1 h-2 bg-gray-200 rounded-full')}>
            <div
              className={cn(
                'h-2 rounded-full transition-all duration-300',
                usesAssessmentScreenLayout ? 'bg-lc-success-500' : 'bg-indigo-600',
              )}
              style={{ width: `${((currentIndex + 1) / displayQuizList.length) * 100}%` }}
            />
          </div>
          {!usesAssessmentScreenLayout ? (
            <span className="text-sm text-gray-600 whitespace-nowrap">
              {currentIndex + 1} / {displayQuizList.length}
            </span>
          ) : null}
        </div>
      </div>

      {/* Quiz */}
      <QuizContainer
        key={currentQuizKey}
        quiz={currentQuiz}
        contentId={contentId}
        sequenceNumber={currentIndex + 1}
        onComplete={handleQuizComplete}
        showResultImmediately={true}
        showHint={showHint}
        quizScriptApi={quizScriptApi}
        sessionId={sessionId}
        onQuizResponse={onQuizResponse}
        streak={solveStatus}
        streakIndex={currentIndex}
        resultDisplayMode="popup"
        assessmentMode={assessmentMode}
        assessmentActionPlacement={assessmentActionPlacement}
        mode={mode}
        onAdvance={handleNext}
        nextLabel={currentIndex < displayQuizList.length - 1 ? t('session.next') : t('session.done')}
      />

      {/* Navigation */}
      {!assessmentMode && (
      <div className="flex justify-between mt-6">
        <button
          onClick={handlePrevious}
          disabled={currentIndex === 0}
          className={`px-6 py-3 rounded-lg font-medium flex items-center gap-2 ${
            currentIndex === 0
              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          {t('session.previous')}
        </button>

        <button
          onClick={handleNext}
          disabled={!currentResult}
          className={`px-6 py-3 rounded-lg font-medium flex items-center gap-2 ${
            currentResult
              ? 'text-white bg-indigo-600 hover:bg-indigo-700'
              : 'text-gray-400 bg-gray-100 cursor-not-allowed'
          }`}
        >
          {currentIndex < displayQuizList.length - 1 ? t('session.next') : t('session.done')}
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>
      )}

      {/* Question Navigator */}
      {!assessmentMode && (
      <div className="p-4 mt-8 bg-gray-50 rounded-lg">
        <p className="mb-3 text-sm text-gray-600">{t('session.jumpToQuestion')}</p>
        <div className="flex flex-wrap gap-2">
          {displayQuizList.map((quiz, index) => {
            const key = getQuizKey(quiz, index)
            const result = results.get(key)
            return (
              <button
                key={key}
                onClick={() => setCurrentIndex(index)}
                className={`w-10 h-10 rounded-lg font-medium text-sm transition-all ${
                  index === currentIndex
                    ? 'bg-indigo-600 text-white ring-2 ring-indigo-300'
                    : result
                      ? result.isCorrect
                        ? 'bg-green-100 text-green-800 border border-green-300'
                        : 'bg-red-100 text-red-800 border border-red-300'
                      : 'bg-white text-gray-600 border border-gray-300 hover:border-indigo-300'
                }`}
              >
                {index + 1}
              </button>
            )
          })}
        </div>
      </div>
      )}
    </div>
  )
}

export default QuizSession
